/**
 * Release announcements + admin draft publishing.
 */

import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  getDocs,
  where,
  arrayUnion,
  Timestamp,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  ReleaseAnnouncement,
  ReleaseAnnouncementDraft,
  ReleaseDraftStatus,
  ReleaseFeature,
  User,
  UserRole,
  EmployeeDesignation,
} from '../types';
import { releaseMatchesUserAudience, hasReleaseAudienceSelection, parseAudienceUserIds } from './releaseAudience';

const COLLECTION = 'releaseAnnouncements';
const DRAFTS_COLLECTION = 'releaseAnnouncementDrafts';

const VALID_ROLES: UserRole[] = [
  'employee',
  'client',
  'admin',
  'designer',
  'architect',
  'engineer',
  'staff',
  'supplier',
  'cpa',
];

const VALID_DESIGNATIONS: EmployeeDesignation[] = [
  'project_manager',
  'estimator',
  'executive_assistant',
  'sales_rep',
  'cfo',
  'coo',
  'office_manager',
];

function parseAudienceDesignations(raw: unknown): EmployeeDesignation[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (d): d is EmployeeDesignation =>
      typeof d === 'string' && VALID_DESIGNATIONS.includes(d as EmployeeDesignation),
  );
}

function parseFeature(raw: unknown): ReleaseFeature | null {
  if (!raw || typeof raw !== 'object') return null;
  const f = raw as Record<string, unknown>;
  const title = typeof f.title === 'string' ? f.title.trim() : '';
  if (!title) return null;
  return {
    title,
    description: typeof f.description === 'string' ? f.description : '',
    ...(Array.isArray(f.bullets)
      ? { bullets: f.bullets.filter((b): b is string => typeof b === 'string' && b.trim().length > 0) }
      : {}),
    ...(typeof f.icon === 'string' && f.icon ? { icon: f.icon } : {}),
    ...(typeof f.imageUrl === 'string' && f.imageUrl ? { imageUrl: f.imageUrl } : {}),
    ...(typeof f.area === 'string' && f.area ? { area: f.area } : {}),
  };
}

function parseRelease(id: string, data: Record<string, unknown>): ReleaseAnnouncement {
  const publishedAtRaw = data.publishedAt;
  const publishedAt =
    publishedAtRaw instanceof Timestamp ? publishedAtRaw.toDate() : new Date(0);
  const audienceRoles = Array.isArray(data.audienceRoles)
    ? (data.audienceRoles.filter(
        (r): r is UserRole => typeof r === 'string' && VALID_ROLES.includes(r as UserRole)
      ) as UserRole[])
    : [];
  const audienceDesignations = parseAudienceDesignations(data.audienceDesignations);
  const audienceUserIds = parseAudienceUserIds(data.audienceUserIds);
  const features = Array.isArray(data.features)
    ? data.features.map(parseFeature).filter((f): f is ReleaseFeature => f !== null)
    : [];
  return {
    id,
    version: typeof data.version === 'string' ? data.version : '',
    title: typeof data.title === 'string' ? data.title : '',
    active: data.active !== false,
    publishedAt,
    audienceRoles,
    ...(audienceDesignations.length ? { audienceDesignations } : {}),
    ...(audienceUserIds.length ? { audienceUserIds } : {}),
    features,
  };
}

function parseDraft(id: string, data: Record<string, unknown>): ReleaseAnnouncementDraft {
  const createdAtRaw = data.createdAt;
  const createdAt =
    createdAtRaw instanceof Timestamp ? createdAtRaw.toDate() : new Date(0);
  const statusRaw = data.status;
  const status: ReleaseDraftStatus =
    statusRaw === 'published' || statusRaw === 'skipped' ? statusRaw : 'pending';
  const audienceRoles = Array.isArray(data.audienceRoles)
    ? (data.audienceRoles.filter(
        (r): r is UserRole => typeof r === 'string' && VALID_ROLES.includes(r as UserRole)
      ) as UserRole[])
    : [];
  const audienceDesignations = parseAudienceDesignations(data.audienceDesignations);
  const audienceUserIds = parseAudienceUserIds(data.audienceUserIds);
  const features = Array.isArray(data.features)
    ? data.features.map(parseFeature).filter((f): f is ReleaseFeature => f !== null)
    : [];
  return {
    id,
    version: typeof data.version === 'string' ? data.version : '',
    title: typeof data.title === 'string' ? data.title : '',
    status,
    createdAt,
    audienceRoles,
    ...(audienceDesignations.length ? { audienceDesignations } : {}),
    ...(audienceUserIds.length ? { audienceUserIds } : {}),
    features,
    ...(typeof data.deployCommit === 'string' ? { deployCommit: data.deployCommit } : {}),
    ...(typeof data.publishedReleaseId === 'string'
      ? { publishedReleaseId: data.publishedReleaseId }
      : {}),
  };
}

/** Convert draft to the shape used by the popup preview. */
export function draftToPreviewRelease(draft: ReleaseAnnouncementDraft): ReleaseAnnouncement {
  return {
    id: draft.id,
    version: draft.version,
    title: draft.title,
    active: true,
    publishedAt: draft.createdAt,
    audienceRoles: draft.audienceRoles,
    ...(draft.audienceDesignations?.length ? { audienceDesignations: draft.audienceDesignations } : {}),
    ...(draft.audienceUserIds?.length ? { audienceUserIds: draft.audienceUserIds } : {}),
    features: draft.features,
  };
}

export function subscribeToLatestRelease(
  callback: (release: ReleaseAnnouncement | null) => void
): () => void {
  const q = query(collection(db, COLLECTION), orderBy('publishedAt', 'desc'), limit(5));
  return onSnapshot(
    q,
    snapshot => {
      const latestActive = snapshot.docs
        .map(d => parseRelease(d.id, d.data() as Record<string, unknown>))
        .find(r => r.active);
      callback(latestActive ?? null);
    },
    error => {
      console.warn('releaseAnnouncements subscription failed:', error);
      callback(null);
    }
  );
}

/** Newest pending draft awaiting admin publish (or null). */
export function subscribeToPendingReleaseDraft(
  callback: (draft: ReleaseAnnouncementDraft | null) => void
): () => void {
  const q = query(collection(db, DRAFTS_COLLECTION), orderBy('createdAt', 'desc'), limit(10));
  return onSnapshot(
    q,
    snapshot => {
      const pending = snapshot.docs
        .map(d => parseDraft(d.id, d.data() as Record<string, unknown>))
        .find(d => d.status === 'pending' && d.features.length > 0);
      callback(pending ?? null);
    },
    error => {
      console.warn('releaseAnnouncementDrafts subscription failed:', error);
      callback(null);
    }
  );
}

export function shouldShowRelease(
  release: ReleaseAnnouncement | null,
  user: User | null
): boolean {
  if (!release || !user || !release.active) return false;
  if (release.features.length === 0) return false;
  if (!releaseMatchesUserAudience(release, user)) return false;
  if (user.seenReleaseIds?.includes(release.id)) return false;
  return true;
}

export async function markReleaseSeen(userId: string, releaseId: string): Promise<void> {
  await updateDoc(doc(db, 'users', userId), {
    seenReleaseIds: arrayUnion(releaseId),
  });
}

async function deactivateActiveReleases(): Promise<void> {
  const activeSnap = await getDocs(
    query(collection(db, COLLECTION), where('active', '==', true))
  );
  if (activeSnap.empty) return;
  const batch = writeBatch(db);
  activeSnap.docs.forEach(d => batch.update(d.ref, { active: false }));
  await batch.commit();
}

/** Admin publishes a pending draft → live release announcement for selected audiences. */
export async function publishReleaseDraft(
  draft: ReleaseAnnouncementDraft,
  options?: {
    title?: string;
    audienceRoles?: UserRole[];
    audienceDesignations?: EmployeeDesignation[];
    audienceUserIds?: string[];
    features?: ReleaseFeature[];
  },
): Promise<string> {
  const audienceRoles =
    options?.audienceRoles !== undefined ? options.audienceRoles : draft.audienceRoles;
  const audienceDesignations =
    options?.audienceDesignations !== undefined
      ? options.audienceDesignations
      : draft.audienceDesignations ?? [];
  const audienceUserIds =
    options?.audienceUserIds !== undefined
      ? parseAudienceUserIds(options.audienceUserIds)
      : parseAudienceUserIds(draft.audienceUserIds);
  const features =
    options?.features && options.features.length > 0 ? options.features : draft.features;
  const title = (options?.title ?? draft.title).trim() || draft.title;
  if (!hasReleaseAudienceSelection(audienceRoles, audienceDesignations, audienceUserIds)) {
    throw new Error('Select at least one audience group.');
  }
  if (features.length === 0) {
    throw new Error('Release must include at least one feature.');
  }
  const existing = await getDocs(
    query(collection(db, COLLECTION), where('version', '==', draft.version), limit(1))
  );
  if (!existing.empty) {
    throw new Error(`Version v${draft.version} is already published.`);
  }

  await deactivateActiveReleases();

  const releaseRef = await addDoc(collection(db, COLLECTION), {
    version: draft.version,
    title,
    active: true,
    audienceRoles,
    ...(audienceDesignations.length ? { audienceDesignations } : {}),
    ...(audienceUserIds.length ? { audienceUserIds } : {}),
    features,
    publishedAt: serverTimestamp(),
    sourceDraftId: draft.id,
  });

  await updateDoc(doc(db, DRAFTS_COLLECTION, draft.id), {
    status: 'published',
    publishedReleaseId: releaseRef.id,
    publishedAt: serverTimestamp(),
  });

  return releaseRef.id;
}

/** Admin permanently skips a draft (won't prompt again). */
export async function skipReleaseDraft(draftId: string): Promise<void> {
  await updateDoc(doc(db, DRAFTS_COLLECTION, draftId), {
    status: 'skipped',
    skippedAt: serverTimestamp(),
  });
}

/** Admin replaces features on a live release (e.g. after regenerating full notes). */
export async function updateLiveReleaseContent(
  releaseId: string,
  patch: Pick<ReleaseAnnouncement, 'title' | 'features' | 'audienceRoles' | 'audienceDesignations' | 'audienceUserIds'>,
): Promise<void> {
  if (!patch.features?.length) {
    throw new Error('Release must include at least one feature.');
  }
  const audienceUserIds = parseAudienceUserIds(patch.audienceUserIds);
  await updateDoc(doc(db, COLLECTION, releaseId), {
    title: patch.title,
    features: patch.features,
    audienceRoles: patch.audienceRoles,
    ...(patch.audienceDesignations?.length
      ? { audienceDesignations: patch.audienceDesignations }
      : { audienceDesignations: [] }),
    ...(audienceUserIds.length ? { audienceUserIds } : { audienceUserIds: [] }),
    updatedAt: serverTimestamp(),
  });
}
